// Libraries
import { useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Toggle03Left } from "@untitled-ui/icons-react";
import { Switch } from "@lockerpm/design";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import store from "#src/store";

// Components
import ConfigSectionWrapperV2 from "#src/commonV2/helper/wrapper/ConfigSection";
import { Enable2FAModal } from "./Enable2FAModal";

// API-related
import workspaceServices, {
  type IWorkspaceItem,
  type IEnabled2FA,
} from "#src/services/workspace";
import { updateCurrentWorkspaceV2 } from "#src/store/slices/auth";

interface IEnable2FAProps {
  workspaceId: string;
  allowedToUpdate: boolean;
  currentWorkspace: IWorkspaceItem;
  setCurrentWorkspace: Dispatch<SetStateAction<IWorkspaceItem>>;
}
const Enable2FA = ({
  workspaceId,
  allowedToUpdate,
  currentWorkspace,
  setCurrentWorkspace,
}: IEnable2FAProps) => {
  const { t } = useTranslation("workspace", { keyPrefix: "security" });

  const [openEnable2FAModal, setOpenEnable2FAModal] = useState<boolean>(false);
  const [callingAPI, setCallingAPI] = useState(false);

  const enabled2fa = async (data: IEnabled2FA) => {
    setCallingAPI(true);
    workspaceServices.enabled_2fa(workspaceId, data).then(() => {
      setCurrentWorkspace({
        ...currentWorkspace,
        ...data,
      })
      store.dispatch(updateCurrentWorkspaceV2({
        ...currentWorkspace,
        ...data,
      }))
      setOpenEnable2FAModal(false);
    }).catch((error) => {
      apiErrorHandler(error, {
        toastMessage: t("notification.enable2FA.fail"),
      });
    }).finally(() => {
      setCallingAPI(false);
    })
  }

  return (
    <>
      <ConfigSectionWrapperV2
        icon={<Toggle03Left className="h-5 w-5 text-gray-v2-500" />}
        headerTitle={t("enable2FA.title")}
      >
        <div className="flex gap-5 p-4 justify-between">
          <p className="text-md font-regular text-gray-v2-900">{t("enable2FA.description")}</p>
          <Switch
            disabled={!allowedToUpdate}
            checked={currentWorkspace.enabled_2fa}
            loading={callingAPI}
            onChange={() => {
              if (!currentWorkspace.enabled_2fa) {
                setOpenEnable2FAModal(true)
              } else {
                enabled2fa({ enabled_2fa: false, enabled_2fa_roles: null })
              }
            }}
          />
        </div>
      </ConfigSectionWrapperV2>
      <Enable2FAModal
        open={openEnable2FAModal}
        callingAPI={callingAPI}
        enabled2fa={enabled2fa}
        onClose={() => {
          setOpenEnable2FAModal(false);
        }}
      />
    </>
  );
};

export default Enable2FA;
 